import { ApiError } from "../api/client";
import { drafts, offlineSupported, type PendingDraft } from "./offlineStore";

/**
 * Drains the queue of drafts saved while offline.
 *
 * Runs when the browser says it is back online, and once at startup: a participant may have closed the tab offline
 * and reopened it with a connection, in which case no `online` event ever fires.
 *
 * A draft is removed only once the server has taken it. A network failure leaves it queued for the next attempt; a
 * 4xx or a GraphQL validation error removes it, because a request the server refuses will be refused identically
 * forever and retrying it every reconnect is a queue that never empties.
 */
type Sender = (payload: never) => Promise<unknown>;
const senders = new Map<string, Sender>();

/** Each screen that saves drafts registers how to send its kind. */
export function registerDraftSender<T>(kind: string, send: (payload: T) => Promise<unknown>): void {
  senders.set(kind, send as Sender);
}

const listeners = new Set<(pending: number) => void>();
export function onPendingChange(listener: (pending: number) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function notify(): Promise<void> {
  const pending = offlineSupported() ? await drafts.count().catch(() => 0) : 0;
  listeners.forEach((listener) => listener(pending));
}

/**
 * Save now if online; queue for later if the network is down. Returns true if the server has it.
 * Only network failures queue: a server refusal is thrown back to the screen so the participant can fix it.
 */
export async function saveOrQueue<T>(
  kind: string,
  id: string,
  payload: T,
  send: (payload: T) => Promise<unknown>,
) {
  try {
    await send(payload);
    if (offlineSupported()) await drafts.remove(id).catch(() => undefined);
    await notify();
    return true;
  } catch (error) {
    if (!(error instanceof ApiError && error.isNetwork) || !offlineSupported()) throw error;
    await drafts.put({ id, kind, payload, queuedAt: Date.now() });
    await notify();
    return false;
  }
}

export async function drainPending(): Promise<{ sent: number; kept: number }> {
  let sent = 0;
  let kept = 0;

  let queued: PendingDraft[];
  try {
    queued = offlineSupported() ? await drafts.all() : [];
  } catch {
    return { sent: 0, kept: 0 };
  }

  for (const draft of queued.sort((a, b) => a.queuedAt - b.queuedAt)) {
    const send = senders.get(draft.kind);
    if (!send) {
      kept += 1;
      continue;
    }
    try {
      await send(draft.payload as never);
      await drafts.remove(draft.id);
      sent += 1;
    } catch (error) {
      const permanent = error instanceof ApiError && !error.isNetwork;
      if (permanent) {
        await drafts.remove(draft.id);
        continue;
      }
      kept += 1;
    }
  }

  await notify();
  return { sent, kept };
}

/** Start draining on reconnect. Returns a teardown for tests. */
export function startOfflineSync(): () => void {
  const onOnline = () => void drainPending();
  window.addEventListener("online", onOnline);
  if (navigator.onLine) void drainPending();
  return () => window.removeEventListener("online", onOnline);
}
